import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { usePartner } from "~/OnboardingApp/hooks";
import { useMetaByPathConfig } from "./useMetaByPathConfig";

// First-party ingest, deliberately NOT routed through trackersFactory /
// gaEventFactory / amplitudeEventFactory: those are CookieYes-gated, so a
// consent rejection would silently zero the onboarding funnel while the report
// still rendered a confident-looking chart. The funnel is internal product
// measurement keyed to a first-party cookie, so it must not depend on a vendor
// consent category. The path also avoids every token content blockers match on
// (event/track/analytics/collect/beacon/pixel/stat/telemetry/metrics/log) —
// see Api::V1::OnboardingStepEventsController.
const STEP_VIEWS_PATH = "/api/v1/onboarding/step_views";

// Flow values mirror OnboardingFlowKind (app/enumerations/onboarding_flow_kind.rb).
// The server rejects anything else with a Sentry drift warning and a 204.
type StepViewFlow = "partner" | "default";

interface StepViewPayload {
  step_key: string;
  flow: StepViewFlow;
  partner_slug?: string;
  qr_code_key?: string;
}

// Fire-and-forget by construction: nothing awaits this, every failure mode is
// swallowed, and there is no retry. A telemetry hiccup must never block or
// surface in the wizard. `keepalive` lets an event emitted immediately before a
// navigation or tab close still reach the server. `credentials: "include"` is
// required for the httponly signed `onboarding_session` cookie to round-trip —
// that cookie IS the funnel's anonymous session identity, and the API lives on
// a separate origin from the SPA in development.
const postStepView = (payload: StepViewPayload): void => {
  const { VITE_API_URL } = import.meta.env;

  try {
    void fetch(`${VITE_API_URL ?? ""}${STEP_VIEWS_PATH}`, {
      method: "POST",
      credentials: "include",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).catch(() => undefined);
  } catch {
    // fetch itself can throw synchronously (unavailable, blocked origin).
    // Telemetry stays silent either way.
  }
};

/**
 * Emits one step view per displayed wizard step, for both the partner and the
 * default flow.
 *
 * Fires on DISPLAY, not on advance. The `useNav` trackers fire from goToNext,
 * which by construction can never see the step a patient abandoned on — the
 * exact number the funnel exists to report.
 */
export const useStepViewTracker = (): void => {
  const { pathname } = useLocation();
  const { metaByPath } = useMetaByPathConfig();
  // usePartner derives the slug from the route synchronously (and owns the
  // ?qr= sessionStorage stash), so neither value waits on the partner fetch.
  const { partnerSlug, qrCodeKey } = usePartner();

  // Keyed on path, not a boolean: React strict-mode remounts re-run effects on
  // a preserved component instance, so the ref survives the second invocation
  // and the same step cannot post twice in a row.
  const emittedPath = useRef<string | null>(null);

  const stepKey = metaByPath[pathname]?.analyticsStem;

  useEffect(() => {
    // A routed path with no metaByPath entry (or a blank stem) has no catalog
    // identity — post nothing rather than an undefined step_key the server
    // would only reject.
    if (!stepKey) return;
    if (emittedPath.current === pathname) return;

    emittedPath.current = pathname;

    postStepView({
      step_key: stepKey,
      flow: partnerSlug ? "partner" : "default",
      ...(partnerSlug ? { partner_slug: partnerSlug } : {}),
      ...(qrCodeKey ? { qr_code_key: qrCodeKey } : {}),
    });
  }, [pathname, stepKey, partnerSlug, qrCodeKey]);
};
